import { convertKeysToLowerCamelCase, DetectedEvent, Device, Files, Frame, IntrinsicsTranslator, iPointTranslator, RadarConfiguration, TargetSource, TrackedTarget, VisualizerProject } from "@app/shared";
import { getProjectById } from "@app/shared/src/api/VisualizerControllerClient.gen";
import { is_sink_eof, Sink } from "@app/shared/src/helpers/serializerHelpers";
import _ from "lodash";
import { generateColors } from "../../../helpers";
import { HomographyTranslator } from "../../../helpers/point-translators/homographyTranslator";

// Type for rectangles in the JSON file
export interface Rectangle {
    bbox: number[];
    cla: number;
    conf: number;
}

// Type for the JSON file structure
export interface FrameRectangles {
    [frameIndex: number]: Rectangle[];
}

export type SimulationResults = {
    targets: { [lastFrameTimestamp: number]: TrackedTarget[] };
    events: { [lastFrameTimestamp: number]: DetectedEvent[] };
};

export type ProjectFrameType = "video" | "radar";

//Right now we just assume the project starts when the video starts - to make this work radar only we would probably need a project start date that time counts from
export const getRadarFrame = (project: ProjectWithData | undefined, timeS: number) => {
    if (!project) return 0;

    const radarFps = project.radarFps ?? 10;
    //Get the radar frame from the video time
    // (video start + videoTime ) - radarStart == time(ms) in radar data
    const radarFrame = Math.floor((((project.videoStartDate?.getTime() ?? 0) + timeS * 1000 - (project.radarStartDate?.getTime() ?? 0)) * radarFps) / 1000);
    return radarFrame;
};

export const getVideoFrame = (project: ProjectWithData | undefined, timeS: number) => {
    if (!project) return 0;

    const videoFps = project.videoFps ?? 20;
    return Math.floor(timeS * videoFps);
};

export const videoFrameToRadarFrame = (project: ProjectWithData | undefined, videoFrame: number) => {
    if (!project) return 0;
    const timeS = videoFrame / (project.videoFps ?? 20);
    const radarFrame = getRadarFrame(project, timeS);
    return radarFrame;
};

export const radarFrameToVideoFrame = (project: { radarFps?: number; radarStartDate: Date | undefined; videoStartDate: Date | undefined; videoFps: number | undefined } | undefined, radarFrame: number) => {
    if (!project) return 0;

    const timeS = radarFrame / (project.radarFps ?? 10);
    const videoFrame = Math.floor((timeS + ((project.radarStartDate?.getTime() ?? 0) - (project.videoStartDate?.getTime() ?? 0)) / 1000) * (project.videoFps ?? 20));
    return videoFrame;
};

const numColors = 50;
const randomColors = generateColors(numColors);
export class ProjectWithData extends VisualizerProject {
    videoOverlays?: FrameRectangles;
    radarFrames?: Frame[];
    projectFps?: number; //Project FPS should always match the data with the highest FPS
    radarFps?: number;
    dataEndDate?: Date;
    pointTranslator?: iPointTranslator;
    simResults?: SimulationResults;
    userResults?: SimulationResults;
    simTargetsByFrame?: { [frameIndex: number]: TrackedTarget[] };
    userTargetsByFrame?: { [frameIndex: number]: TrackedTarget[] };
    projectFrameType: ProjectFrameType = "radar";
    simTargets?: TrackedTarget[];
    userTargets?: TrackedTarget[];
    pointCloudColors: string[] = randomColors;
    radarParsingError?: string;

    //Min/max from our data
    getDataStartDate = () => {
        if (!this.videoStartDate && !this.radarStartDate) return;
        return (this.videoStartDate ?? Number.MAX_VALUE) < (this.radarStartDate ?? Number.MAX_VALUE) ? this.videoStartDate : this.radarStartDate;
    };
}

// Runtime-only ProjectWithData keys to strip before saving to the server. The `satisfies`
// constraint makes a future rename in the class above produce a compile error here instead of
// silently letting the renamed field leak into the save payload.
export const PROJECT_RUNTIME_ONLY_KEYS = [
    "videoOverlays", "radarFrames", "projectFps", "radarFps", "dataEndDate",
    "pointTranslator", "simResults", "userResults", "simTargetsByFrame",
    "userTargetsByFrame", "projectFrameType", "simTargets", "userTargets",
    "pointCloudColors", "radarParsingError", "getDataStartDate",
] as const satisfies readonly (keyof ProjectWithData)[];

export async function downloadFile(projectId: string, fileName: string) {
    const projectFileFolder = `visualizer/${projectId}`;
    const fileUrl = await Files.getPublicUrl(projectFileFolder + "/" + fileName);

    if (!fileUrl) return undefined;

    const resp = await fetch(fileUrl);

    if (!resp.ok) {
        console.log(`HTTP error! status: ${resp.status}`);
        return undefined;
    }
    return resp;
}

export function getTargetsByFrame(simulation: SimulationResults | undefined, project: ProjectWithData) {
    var retVal: { [frameIndex: number]: TrackedTarget[] } = {};
    if (!simulation) return retVal;

    Object.values(simulation?.targets ?? []).forEach((targets) => {
        //The tracked target should have an entry for each frame that it is in, even if the history was thinned or we didn't get a return for that frame (we can interpolate between them)
        targets.forEach((target) => {
            var firstFrame = target.firstFrameTracked;
            var lastFrame = target.lastFrameTracked;

            if (target.source === TargetSource.Camera && project.projectFrameType === "radar") {
                firstFrame = videoFrameToRadarFrame(project, firstFrame);
                lastFrame = videoFrameToRadarFrame(project, lastFrame);
            } else if (target.source === TargetSource.Radar && project.projectFrameType === "video") {
                firstFrame = radarFrameToVideoFrame(project, firstFrame);
                lastFrame = radarFrameToVideoFrame(project, lastFrame);
            }

            for (var frame = firstFrame; frame <= lastFrame; frame++) {
                if (!retVal[frame]) {
                    retVal[frame] = [];
                }
                retVal[frame].push(target);
            }
        });
    });

    return retVal;
}

export async function loadProject(projectId: string, abortSignal: AbortSignal, setLoadingMessage?: (message?: string) => void): Promise<ProjectWithData | undefined> {
    const retVal = await getProjectById(projectId);
    console.log("Loading project " + projectId, retVal);

    var radarData: Frame[] = [];
    var overlayData: FrameRectangles = [];
    var radarFps = 10;
    var deviceSettings: Device | undefined = retVal?.device;
    var pointTranslator: iPointTranslator | undefined;
    var simResults: SimulationResults | undefined;
    var userResults: SimulationResults | undefined;
    var simTargetsByFrame: { [frameIndex: number]: TrackedTarget[] } = {};
    var userTargetsByFrame: { [frameIndex: number]: TrackedTarget[] } = {};
    var simTargets: TrackedTarget[] = [];
    var userTargets: TrackedTarget[] = [];
    var projectFrameType: ProjectFrameType = "radar";
    var radarParsingError: string | undefined;
    var firstRadarFrameNumber = 0;

    if (retVal?.videoFile) {
        projectFrameType = "video";
    }

    //If we have radar json data, load that - otherwise load the binary
    if (retVal?.radarDataBinaryFile) {
        setLoadingMessage?.("Downloading radar binary data...");
        const binaryData = await (await downloadFile(retVal.id!, retVal.radarDataBinaryFile))?.arrayBuffer();

        setLoadingMessage?.("Parsing radar binary data...");
        var radarDataEndDate: Date | undefined;

        if (binaryData && binaryData.byteLength > 0) {
            var curFrameNum = 0;
            radarData = new Array<Frame>();
            var dataSink = Sink(binaryData, 0, true);
            while (true) {
                if (abortSignal.aborted) throw new Error("Loading aborted");
                try {
                    var frame = await new Frame().deserialize(dataSink);
                    curFrameNum++;
                    if (!frame) continue;

                    radarData.push(frame);
                } catch (e) {
                    radarParsingError = "Error deserializing radar frame " + curFrameNum + ": " + e;
                    console.error(radarParsingError);
                    break;
                }

                if (is_sink_eof(dataSink)) {
                    break;
                }
            }
            if (radarData.length > 2) {
                radarFps = Math.round(1000 / ((radarData[1].header.timestamp - radarData[0].header.timestamp) / 200000));
            }
            if (retVal.radarStartDate) {
                radarDataEndDate = new Date(retVal.radarStartDate.getTime() + (radarData.length / radarFps) * 1000);
            }

            //console.log(`Deserialized ${radarData.length} frames from binary radar file, fps: ${radarFps}`);
        }
    }
    firstRadarFrameNumber = radarData?.[0]?.header?.frameNumber ?? 0;

    //Now load our overlay data into the project - do we need a progress bar for this?
    if (retVal?.inferrenceJsonFile) {
        setLoadingMessage?.("Downloading inference overlay data...");
        const tempFile = await (await downloadFile(retVal.id!, retVal.inferrenceJsonFile))?.text();

        setLoadingMessage?.("Parsing inference overlay data...");
        try {
            overlayData = JSON.parse(tempFile ?? "");
        } catch (e) {
            setLoadingMessage?.("Error parsing overlay data");
            console.error("Error parsing overlay data: " + e);
        }
        //console.log(`Parsed ${Object.keys(overlayData).length} frames from overlay file`);
    }

    //Now load our device settings file if we have one
    if (retVal?.deviceSettingsJsonFile) {
        setLoadingMessage?.("Downloading device settings file...");
        const tempFile = await (await downloadFile(retVal.id!, retVal.deviceSettingsJsonFile))?.text();

        setLoadingMessage?.("Processing device settings file...");
        try {
            deviceSettings = new Device(convertKeysToLowerCamelCase(JSON.parse(tempFile ?? "")));
        } catch (e) {
            setLoadingMessage?.("Error parsing device settings file");
            console.error("Error parsing device settings file: " + e);
        }
    }

    //Create the homography helper
    if (deviceSettings) {
        setLoadingMessage?.("Creating translation helper...");

        var mode2d = true;
        if ((deviceSettings?.sensorConfig?.$type ?? "").indexOf("RadarConfiguration") > 0) {
            console.log("Sensor config is a RadarConfiguration, setting 2D mode based on chirpConfig");
            mode2d = ((deviceSettings?.sensorConfig as RadarConfiguration)?.chirpConfig?.indexOf("channelCfg 15 7 0") ?? -1) < 0;
        }

        if (retVal?.useIntrinsicTranslation || !deviceSettings?.homographySourcePoints || !deviceSettings?.homographyDestinationPoints || deviceSettings?.homographySourcePoints?.length === 0 || deviceSettings?.homographyDestinationPoints?.length === 0) {
            pointTranslator = new IntrinsicsTranslator();
            //TODO: These should be stored with the project instead of coming from DeviceSettings, just didn't want to database change ATM
            const combinedPitch = (retVal?.deviceTiltDegrees ?? 0) + ((deviceSettings?.fusionOffsetPitch ?? 0) * -180) / Math.PI;
            const combinedRoll = (retVal?.deviceRollDegrees ?? 0) + ((deviceSettings?.fusionOffsetRoll ?? 0) * 180) / Math.PI;
            pointTranslator.calculateFromParameters(mode2d, retVal?.deviceHeight ?? deviceSettings.height, combinedPitch, combinedRoll, retVal?.estimatedReturnHeight ?? 0);
        } else {
            pointTranslator = new HomographyTranslator();
            pointTranslator.calculateFromSettings(deviceSettings, retVal?.deviceTiltDegrees ?? 0, retVal?.deviceRollDegrees ?? 0, retVal?.estimatedReturnHeight ?? 0);
        }
    }

    //If we have an output file uploaded, load it too
    if (retVal?.userResultsJsonFile) {
        setLoadingMessage?.("Downloading user results JSON file...");
        const tempFile = await (await downloadFile(retVal.id!, retVal.userResultsJsonFile))?.text();

        setLoadingMessage?.("Parsing user results JSON file...");
        try {
            userResults = convertKeysToLowerCamelCase(JSON.parse(tempFile ?? ""));
            userTargets = Object.values(userResults?.targets ?? []).flat();
        } catch (e) {
            setLoadingMessage?.("Error parsing user results JSON file");
            console.error("Error parsing user results JSON file: " + e);
        }
    }
    if (retVal?.simResultsJsonFile) {
        setLoadingMessage?.("Downloading simulation results JSON file...");
        const tempFile = await (await downloadFile(retVal.id!, retVal.simResultsJsonFile))?.text();

        setLoadingMessage?.("Parsing simulation results JSON file...");
        simResults = convertKeysToLowerCamelCase(JSON.parse(tempFile ?? ""));
        simTargets = Object.values(simResults?.targets ?? [])
            .flat()
            .map((target, index) => {
                target.listIndex = index;
                return target;
            });
        //Adjust radar frame numbers to be relative to the first radar frame.  Also add a first video frame tracked for sorting
        simTargets.forEach((target, index) => {
            target.firstVideoFrameTracked = target.firstFrameTracked;

            if (target.source === TargetSource.Radar) {
                target.firstFrameTracked -= firstRadarFrameNumber;
                target.lastFrameTracked -= firstRadarFrameNumber;
                target.firstVideoFrameTracked = radarFrameToVideoFrame({ ...retVal, radarFps }, target.firstFrameTracked);
                target.targetHistory.forEach((history) => {
                    history.frameNumber -= firstRadarFrameNumber;
                });
            }
        });
        simTargets = _.orderBy(simTargets, (t) => t.firstVideoFrameTracked, "asc");
        simTargets.forEach((target, index) => {
            //Set the index for the target so we can scroll to it later
            target.listIndex = index;
        });
    }

    setLoadingMessage?.(undefined);
    var retValWithData = new ProjectWithData();
    Object.assign(retValWithData, {
        ...retVal,
        dataEndDate: radarDataEndDate,
        videoOverlays: overlayData,
        radarFrames: radarData,
        radarFps: radarFps,
        device: deviceSettings,
        pointTranslator,
        simResults,
        simTargetsByFrame,
        simTargets,
        userResults,
        userTargetsByFrame,
        userTargets,
        projectFrameType,
        radarParsingError,
    } as Partial<ProjectWithData>);

    if (simResults) {
        retValWithData.simTargetsByFrame = getTargetsByFrame(simResults, retValWithData);
    }
    if (userTargets) {
        retValWithData.userTargetsByFrame = getTargetsByFrame(userResults, retValWithData);
    }
    return retValWithData;
}

export function hasChangesToProject(a: Partial<ProjectWithData>, b: Partial<ProjectWithData>) {
    const fileChanges = a.videoStartDate !== b.videoStartDate || a.radarStartDate !== b.radarStartDate || a.radarDataBinaryFile !== b.radarDataBinaryFile || a.videoFile !== b.videoFile || a.deviceSettingsJsonFile !== b.deviceSettingsJsonFile || a.simResultsJsonFile !== b.simResultsJsonFile || a.userResultsJsonFile !== b.userResultsJsonFile || a.inferrenceJsonFile !== b.inferrenceJsonFile || a.videoFps !== b.videoFps;
    const hasHomographyChanges = a.device?.homographySourcePoints?.length !== b.device?.homographySourcePoints?.length;
    const hasFacilityChanges = a.facilityId !== b.facilityId;
    const hasDeviceChanges = a.device?.id !== b.device?.id;
    const hasTrackerChanges = a.trackerConfig !== b.trackerConfig;
    const hasDevicePositionChanges = a.devicePositionX !== b.devicePositionX || a.devicePositionY !== b.devicePositionY || a.deviceHeight !== b.deviceHeight || a.deviceHeadingDegrees !== b.deviceHeadingDegrees || a.deviceTiltDegrees !== b.deviceTiltDegrees || a.deviceRollDegrees !== b.deviceRollDegrees || a.estimatedReturnHeight !== b.estimatedReturnHeight;
    const hasNameChanges = a.name !== b.name;
    const hasBoundaryChanges = a.boundaryPoints?.length !== b.boundaryPoints?.length;
    const hasCameraAoiChanges = a.cameraAoiPoints?.length !== b.cameraAoiPoints?.length;

    return fileChanges || hasHomographyChanges || hasFacilityChanges || hasDeviceChanges || hasTrackerChanges || hasDevicePositionChanges || hasNameChanges || hasBoundaryChanges || hasCameraAoiChanges;
}
